import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { ventasAPI, type Factura } from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { formatDateOnlyEsAR, toNum } from "@/lib/utils";
import { useDataRefresh, type RefreshEvent } from "@/lib/dataRefresh";
import {
  comprobanteListFilters,
  comprobanteRowKey,
  isEstadoCae,
  isEstadoFx,
  labelEstadoComprobante,
  sumComprobantesTotal,
} from "./clienteComprobantes";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;
const COMPROBANTE_REFRESH_EVENTS: RefreshEvent[] = ["facturas", "ventas"];

interface ClienteFacturasProps {
  clienteId?: number;
  clienteNombre?: string;
}

function tipoComprobanteOf(factura: Factura): string {
  return (factura.tipoComprobante || "").trim();
}

/** NC-A / NC-B / NC-X. Facturas A/B/X no. */
function isNotaCredito(factura: Factura): boolean {
  return tipoComprobanteOf(factura).toUpperCase().startsWith("NC-");
}

function formatPaymentMethods(factura: Factura): string {
  const methodLabels: Record<string, string> = {
    efectivo: "Efectivo",
    transferencia: "Transfer.",
    tarjeta_credito: "T.Créd.",
    tarjeta_debito: "T.Déb.",
    cheque: "Cheque",
    dolares: "Dólares",
    "cuenta corriente": "Cuenta corriente",
    cuenta_corriente: "Cuenta corriente",
  };
  const fromPagos = (factura.pagos || [])
    .map((p) => p.metodoPago)
    .filter((m): m is string => Boolean(m));
  const methods =
    fromPagos.length > 0
      ? fromPagos
      : factura.metodoPago
        ? [factura.metodoPago]
        : [];
  if (methods.length === 0) return "-";
  return methods.map((m) => methodLabels[m] || m).join(" + ");
}

export function ClienteFacturas({ clienteId, clienteNombre }: ClienteFacturasProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState<Factura[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [clienteId, dateFrom, dateTo, debouncedSearch, pageSize]);

  const loadPage = useCallback(async () => {
    if (clienteId == null) {
      setRows([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const response = await ventasAPI.listFacturasPage({
        cliente: clienteId,
        sin_mes: 1,
        page,
        page_size: pageSize,
        fecha__gte: dateFrom || undefined,
        fecha__lte: dateTo || undefined,
        ...comprobanteListFilters(debouncedSearch),
      });
      if (seq !== requestSeq.current) return;
      setRows(response.results || []);
      setTotalCount(response.count ?? 0);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      console.error(e);
      setRows([]);
      setTotalCount(0);
      setError("No se pudieron cargar los comprobantes");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [clienteId, page, pageSize, dateFrom, dateTo, debouncedSearch]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const onExternalRefresh = useCallback(() => {
    void loadPage();
  }, [loadPage]);
  useDataRefresh(COMPROBANTE_REFRESH_EVENTS, onExternalRefresh);

  const formatDate = (dateString: string) => formatDateOnlyEsAR(dateString);
  const pageTotal = sumComprobantesTotal(rows);
  const hasFilters = Boolean(searchQuery.trim() || dateFrom || dateTo);

  const handleDownloadPDF = async (factura: Factura) => {
    if (!factura.id) {
      toast.error("No se pudo generar el PDF");
      return;
    }
    try {
      const blob = await ventasAPI.downloadFacturaPDF(factura.id);
      if (blob) {
        const win = await openPdfTab(blob, undefined, { path: `/api/facturas/${factura.id}/pdf/` });
        if (!win) {
          toast.error("Pop-up bloqueado. Por favor, habilite los pop-ups para este sitio.");
        } else {
          toast.success("PDF abierto en una nueva pestaña");
        }
      } else {
        toast.error("No se pudo generar el PDF");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error descargando PDF");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-semibold">
          Facturas - {clienteNombre || "Consumidor Final"}
        </h3>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <Input
            type="text"
            placeholder="Buscar por número, cliente o estado..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
        <div>
          <DateInput
            placeholder="Desde"
            value={dateFrom}
            onChange={(ymd) => setDateFrom(ymd)}
            className="w-full"
          />
        </div>
        <div>
          <DateInput
            placeholder="Hasta"
            value={dateTo}
            onChange={(ymd) => setDateTo(ymd)}
            className="w-full"
          />
        </div>
      </div>

      {clienteId == null ? (
        <div className="text-center py-8 text-muted-foreground">
          No se pudo identificar el cliente
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">Cargando comprobantes...</div>
      ) : error && rows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">{error}</div>
      ) : rows.length > 0 ? (
        <>
          <div className="border border-input rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium">Nro Factura</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Cliente</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Total</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Estado</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Pago</th>
                  <th className="text-center py-3 px-4 text-sm font-medium">CAE</th>
                  <th className="text-center py-3 px-4 text-sm font-medium">PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((factura) => (
                  <tr
                    key={comprobanteRowKey(factura)}
                    className="border-t border-input hover:bg-muted/50"
                  >
                    <td className="py-3 px-4 text-sm font-mono">{factura.numero}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(factura.fecha)}</td>
                    <td className="py-3 px-4 text-sm">{factura.cliente?.nombre}</td>
                    <td className="py-3 px-4 text-sm text-right font-semibold">
                      ${toNum(factura.total).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          factura.estado === 'Pendiente'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : factura.estado === 'Confirmado - CAE/AFIP'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                          {labelEstadoComprobante(factura.estado)}
                        </span>
                        {isNotaCredito(factura) && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 min-w-[20px] justify-center" title="Nota de Crédito">
                            {tipoComprobanteOf(factura)}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{formatPaymentMethods(factura)}</td>
                    <td className="py-3 px-4 text-sm text-center">{factura.cae || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      {factura.estado !== 'Pendiente' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleDownloadPDF(factura)}
                          title="Descargar PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted border-t-2 border-input">
                <tr>
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-right">Total de esta página:</td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-lg">
                    ${pageTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center justify-end text-sm text-muted-foreground">
            <span>
              {rows.filter(f => isEstadoCae(f.estado)).length} CAE | {' '}
              {rows.filter(f => isEstadoFx(f.estado)).length} FX | {' '}
              {rows.filter(f => f.estado === 'Pendiente').length} Pendientes
            </span>
          </div>

          <Pagination
            currentPage={page}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          {hasFilters
            ? "No se encontraron facturas con los filtros aplicados"
            : "No hay facturas para mostrar"}
        </div>
      )}
    </div>
  );
}
